import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { UserButton, SignedIn, SignedOut } from '@clerk/clerk-react';
import {
  MapIcon,
  HomeIcon,
  DocumentArrowUpIcon,
  FolderIcon,
  Cog6ToothIcon,
  UserCircleIcon,
  Bars3Icon,
  XMarkIcon
} from '@heroicons/react/24/outline';

// Check if Clerk is configured
const CLERK_ENABLED = !!process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

export default function Navbar() {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navigation = [
    { name: 'Dashboard', href: '/', icon: HomeIcon },
    { name: 'Voter Map', href: '/map', icon: MapIcon },
    { name: 'Import Data', href: '/import', icon: DocumentArrowUpIcon },
    { name: 'Datasets', href: '/datasets', icon: FolderIcon },
    { name: 'Settings', href: '/settings', icon: Cog6ToothIcon }
  ];

  return (
    <nav className="bg-white shadow-sm border-b border-gray-200">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <MapIcon className="h-8 w-8 text-blue-600 mr-2" />
            <span className="text-xl font-bold text-gray-900">Election Fabric</span>
            <span className="text-sm text-gray-500 ml-2 hidden sm:inline">Voter Intelligence</span>
          </Link>

          {/* Desktop Navigation Links */}
          <div className="hidden lg:flex space-x-4 xl:space-x-8">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${isActive
                      ? 'text-blue-600 bg-blue-50'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                >
                  <item.icon className="h-4 w-4 mr-2" />
                  {item.name}
                </Link>
              );
            })}
          </div>

          {/* Desktop User Menu */}
          <div className="hidden lg:flex items-center gap-4">
            <span className="text-sm text-gray-600 hidden xl:block">AI-Powered Voter Analysis</span>
            {CLERK_ENABLED ? (
              <>
                <SignedIn>
                  <UserButton
                    afterSignOutUrl="/sign-in"
                    appearance={{
                      elements: {
                        avatarBox: 'h-8 w-8'
                      }
                    }}
                  />
                </SignedIn>
                <SignedOut>
                  <Link
                    to="/sign-in"
                    className="flex items-center px-3 py-2 rounded-md text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                  >
                    <UserCircleIcon className="h-5 w-5 mr-1" />
                    Sign In
                  </Link>
                </SignedOut>
              </>
            ) : (
              <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                <UserCircleIcon className="h-5 w-5 text-gray-500" />
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
          >
            {mobileMenuOpen ? (
              <XMarkIcon className="h-6 w-6" />
            ) : (
              <Bars3Icon className="h-6 w-6" />
            )}
          </button>
        </div>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-200">
            <div className="space-y-1">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center px-4 py-3 rounded-md text-base font-medium transition-colors ${isActive
                        ? 'text-blue-600 bg-blue-50'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                  >
                    <item.icon className="h-5 w-5 mr-3" />
                    {item.name}
                  </Link>
                );
              })}
            </div>

            {/* Mobile User Section */}
            <div className="mt-4 pt-4 border-t border-gray-200 px-4">
              {CLERK_ENABLED ? (
                <>
                  <SignedIn>
                    <div className="flex items-center gap-3">
                      <UserButton
                        afterSignOutUrl="/sign-in"
                        appearance={{
                          elements: {
                            avatarBox: 'h-10 w-10'
                          }
                        }}
                      />
                      <span className="text-sm text-gray-600">Your Account</span>
                    </div>
                  </SignedIn>
                  <SignedOut>
                    <Link
                      to="/sign-in"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center px-4 py-3 rounded-md text-base font-medium text-blue-600 hover:bg-blue-50"
                    >
                      <UserCircleIcon className="h-5 w-5 mr-3" />
                      Sign In
                    </Link>
                  </SignedOut>
                </>
              ) : (
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                    <UserCircleIcon className="h-6 w-6 text-gray-500" />
                  </div>
                  <span className="text-sm text-gray-600">Guest User</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
